import LocationIcon from '@/components/icons/parking/Location';
import type { AreaInfoDetail, ParkingRecord } from '@/services/dto/parking';
import { useTranslation } from 'react-i18next';
import ParkingActionButtons from './ParkingActionButtons';
import { addCommaAfterNumber } from '@/utils/parking';
import { useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores';
import { useMemo } from 'react';

interface HeaderHorizontalProps {
    result: ParkingRecord;
    isUseLocation?: boolean;
    areaInfo: AreaInfoDetail | null;
    onBack: () => void;
    handleDirectionGuide: () => void;
}

export default function HeaderHorizontal({ result, isUseLocation, areaInfo, onBack, handleDirectionGuide }: HeaderHorizontalProps) {
    const { t } = useTranslation();
    const handleSearchAgain = () => {
        onBack();
    };
    const formattedLicenseplate = addCommaAfterNumber(result.licenseplate);
    const { playTick } = useTickSoundInteraction();

    const buildingName = useConfigStore((state) => state.config.server?.building?.name?.ko) ?? t('common.lotteWorldMall');

    return (
        <div
            className={`widescreen:flex widescreen:h-[calc(589*var(--vh-unit))] fullscreen:widescreen:h-[calc(756*var(--vh-unit))] hidden w-[calc(546*var(--vw-unit))] flex-col items-center justify-between`}
        >
            <div className={`fullscreen:h-[calc(589*var(--vh-unit))] flex w-[calc(546*var(--vw-unit))] flex-col gap-[40px]`}>
                <div
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    data-region-nav
                    data-region-order={3}
                    tabIndex={3}
                    data-interactive
                    aria-label={`${t('parking.header.vehicleNumber')} ${formattedLicenseplate}`}
                    className="focus-border flex h-[calc(160*var(--vh-unit))] flex-col items-center gap-[calc(10*var(--vh-unit))] self-stretch rounded-lg border-[calc(2*var(--vh-unit))] border-[var(--gray-color-ccc,#CCC)] bg-[var(--color-bg-white)] pt-[calc(24*var(--vh-unit))]"
                >
                    <div className="text-center">
                        <div className="text-bg-main01-to-white mb-[calc(10*var(--vh-unit))] text-[calc(20*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-medium">
                            {t('parking.header.vehicleNumber')}
                        </div>
                        <div className="number text-[calc(48*var(--vh-unit))] leading-[calc(52*var(--vh-unit))] font-bold text-[var(--color-text-main01)]">
                            {result.licenseplate}
                        </div>
                    </div>
                </div>
                {isUseLocation && (
                    <div
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                playTick();
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                        data-region-nav
                        data-region-order={4}
                        tabIndex={4}
                        data-interactive
                        aria-label={`${areaInfo?.parkArea}-${buildingName}-${areaInfo?.parkStair}`}
                        className="focus-border flex h-[calc(134*var(--vh-unit))] w-full flex-col items-center justify-center gap-[calc(10*var(--vh-unit))] self-stretch"
                    >
                        <div className="flex items-center gap-[calc(8*var(--vh-unit))] text-[var(--color-text-color-99938f)]">
                            <LocationIcon style={{ width: 'calc(37*var(--vw-unit))', height: 'calc(36*var(--vh-unit))' }} />
                            <span className="text-center text-[calc(26*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-semibold tracking-[-0.572px] text-[var(--color-text-sub01)]">
                                {areaInfo?.parkArea}-{buildingName}-{areaInfo?.parkStair}
                            </span>
                        </div>
                    </div>
                )}
            </div>
            <ParkingActionButtons
                isUseLocation={isUseLocation ?? false}
                onSearchAgain={handleSearchAgain}
                translationPrefix="parking.header"
                handleDirectionGuide={handleDirectionGuide}
            />
        </div>
    );
}
